/*
 * AnalyticsTabRoute.jsx
 *
 * This component is a wrapper around React Router's Route, used for the Analytics dashboard tabs.
 * It provides:
 *   - Conditional rendering of routes (via the 'condition' prop)
 *   - Deduplicated hit tracking: calls loadRecordHit only if a hit for the current URL has not already been recorded
 *   - Avoids logging hits for redirect routes (e.g., when rendering a <Redirect />), to prevent double hits on initial navigation/redirects
 *   - Manual referrer tracking for SPA navigation: passes the previous URL as the referrer for hit tracking
 *
 * This ensures that only user-facing Analytics tab navigations are logged as hits, and not intermediate redirects or duplicate renders.
 */
import React from 'react';
import { Route } from 'react-router-dom';
import { loadRecordHit } from 'js/utils/loadRecordHit';
import { startTab, endTab } from 'js/analytics/autotest/tabActivityTracking';

// Global variable to track the last Analytics URL for SPA referrer
if (typeof window !== 'undefined' && !window.__lastAnalyticsUrl) {
  window.__lastAnalyticsUrl = window.location.href;
}

export const AnalyticsTabRoute = ({
  condition = true,
  component: Component,
  ...routeProps
}) =>
  condition ? (
    <Route
      {...routeProps}
      render={(props) => {
        const element = <Component {...props} />;
        if (
          element &&
          element.props &&
          element.props.history &&
          element.props.history.action === 'REPLACE'
        ) {
          return element;
        }

        if (
          window.Aedu &&
          window.Aedu.hit_data &&
          window.Aedu.hit_data.url === window.location.href
        ) {
          window.__lastAnalyticsUrl = window.location.href;
          return element;
        }
        // Guard against React re-invoking this render for the same URL before the
        // async loadRecordHit below has updated hit_data.url — without this a single
        // tab switch can double-record the hit and double-emit telemetry.
        if (window.__lastAnalyticsUrl === window.location.href) {
          return element;
        }
        // Use the tracked last URL as the referrer for SPA navigation
        const referrer = window.__lastAnalyticsUrl || document.referrer;
        window.__lastAnalyticsUrl = window.location.href;
        // Analytics ToS v2: close out the outgoing tab-hit (emits with the still
        // current hit_id) and immediately begin the new tab-hit, BOTH before the
        // async loadRecordHit re-keys window.Aedu.hit_data. Tracking starts at tab
        // entry (not after the hit round-trip) so a fast/cached tab's load still
        // registers; the new hit_id is read from the global at emit time.
        endTab('route_change');
        startTab();
        loadRecordHit(true, referrer);
        return element;
      }}
    />
  ) : null;
